import { config } from './config';

/**
 * Helpers for the MCP section on the dashboard's Developers screen.
 * Kept pure (no React) so it is easy to unit test and reuse.
 */

/** Production MCP server. */
export const MCP_URL_PRODUCTION = 'https://mcp.cloudwaitress.com/mcp';

/** MCP server used by every other environment (dev, test, local). */
export const MCP_URL_TEST = 'https://mcp.cloudwaitress-test.xyz/mcp';

/** Picks the MCP server for the API url the dashboard is pointed at. */
export function mcpUrlFromApiUrl(apiUrl: string): string {
  return apiUrl.indexOf('api.cloudwaitress.com') !== -1 ? MCP_URL_PRODUCTION : MCP_URL_TEST;
}

export const MCP_URL = process.env.MCP_URL || mcpUrlFromApiUrl(config.urls.api);

/**
 * Classifies an api key's restaurant list so the UI can label the key and warn when
 * it can't reach any restaurant.
 */
export function mcpKeyScope(restaurants: string[] | undefined): 'single' | 'none' | 'multiple' {
  const count = (restaurants || []).length;
  if (count === 0) return 'none';
  if (count === 1) return 'single';
  return 'multiple';
}

/** The MCP server's OAuth callback. */
export function mcpCallbackURL(): string {
  return MCP_URL.replace(/\/mcp$/, '') + '/oauth/callback';
}

/**
 * Validates a `?redirect=` that points outside the dashboard. Only this
 * environment's MCP server is ever legitimate.
 *
 * The check is on the parsed origin and path, never the string: a prefix
 * test is what an open redirect exploits, since
 * `https://mcp.cloudwaitress.com.evil.test/` starts with the right
 * characters. The query is kept (it carries the request id); a fragment is
 * refused.
 */
export function mcpReturnURL(redirect: string | undefined): string | null {
  if (!redirect) return null;
  let target: URL;
  let expected: URL;
  try {
    target = new URL(redirect);
    expected = new URL(mcpCallbackURL());
  } catch {
    return null;
  }
  if (target.origin !== expected.origin) return null;
  if (target.pathname !== expected.pathname) return null;
  if (target.hash !== '') return null;
  return target.toString();
}
